import useCheckPermission from "@/Hooks/useCheckPermission";
import { sideTabSchema } from "@/Routes/sidebar.utility";
import {
  Combobox,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
  Transition,
} from "@headlessui/react";
import { LinkIcon, MagnifyingGlassIcon } from "@heroicons/react/24/solid";
import { TBasicObject } from "@repo/api-services/API-TYPES";
import { TextInputBase } from "@repo/ui/index.tsx";
import { inputUtility } from "@repo/utilities/input.utility.ts";

import { stringUtility } from "@repo/utilities/string.utility.ts";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

type CTSearchBarProps = {
  className?: string;
  TABS: sideTabSchema[];
};

function QuickSearch({ className, TABS }: CTSearchBarProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState("");
  const { permissionsMap } = useCheckPermission();

  const flatMapped = TABS?.flatMap((data) =>
    data?.subTabs?.length ? data.subTabs : data
  );

  // This will hide the non-view access tabs to be selected
  const permissionCheckedTabs = Array.isArray(permissionsMap)
    ? flatMapped
    : flatMapped.filter((tabUnit) => {
      const permissions =
        permissionsMap?.[
        stringUtility.replaceCharAll(String(tabUnit.code), "-", "_")
        ];
      const viewPermission = permissions?.find(
        (permissionUnit: TBasicObject) =>
          String(permissionUnit.name).includes("view")
      );
      if (viewPermission) {
        return tabUnit;
      }
    }) || [];
  const searchedModule =
    query === ""
      ? permissionCheckedTabs
      : permissionCheckedTabs
        ?.flat()
        ?.filter((tab) =>
          tab?.name?.toLocaleLowerCase()?.includes(query?.toLocaleLowerCase())
        );

  // CUSTOM HOOK/s
  const navigate = useNavigate();

  useEffect(() => {
    window.addEventListener("keydown", (e) => {
      if (e.key === "|") {
        inputRef?.current?.focus();
      }
    });
    return () => {
      window.removeEventListener("keydown", () => { });
    };
  }, []);

  return (
    <div className={`relative max-md:hidden h-10 w-5/12 z-30 ${className}`}>
      <Combobox
        onChange={(value: { path: string }) => {
          navigate(value?.path);
        }}
        immediate
      >
        <section className="relative">
          <span
            className="h-6 px-2 text-sm flex items-center justify-center
           font-medium rounded-md bg-extraLightGray/50 
           text-gray absolute left-2 top-1/2 -translate-y-1/2 z-50"
          >
            shift + \
          </span>

          <ComboboxInput
            value={query}
            as={TextInputBase}
            ref={inputRef}
            autoComplete="off"
            placeholder="Quick search modules..."
            onChange={inputUtility.handleGlobalTextValidation(setQuery, 20)}
            icon={<MagnifyingGlassIcon />}
            className="pl-20 border-none ring-1 ring-extraLightGray"
          />
        </section>

        {/* Options */}
        <Transition
          leave="transition ease-in duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
          afterLeave={() => {
            setQuery("");
            inputRef.current?.blur();
          }}
        >
          <ComboboxOptions
            id="hide-scrollbar"
            className="w-full mt-2 rounded-lg border border-extraLightGray/80 shadow-lg
       bg-white max-h-96 overflow-y-auto"
          >
            <p className="px-4 py-2 sticky top-0 bg-white text-sm font-semibold text-body border-b border-extraLightGray ">
              {searchedModule?.length
                ? "Jump directly to any module listed bellow"
                : "No results found"}
            </p>
            {searchedModule.map((module) => {
              return (
                <ComboboxOption
                  key={module.path}
                  value={module}
                  className="group data-[focus]:bg-primary/10"
                >
                  <button
                    type="button"
                    className="px-4 w-full h-10 flex items-center text-subHeading gap-2 
                group-data-[selected]:bg-primary group-data-[selected]:text-white "
                  >
                    <span
                      className="flex items-center justify-center  h-6 w-6 p-1
                border border-lightGray rounded-lg"
                    >
                      <LinkIcon />
                    </span>

                    <p className="text-sm font-medium  ">{module.name}</p>
                  </button>
                </ComboboxOption>
              );
            })}
          </ComboboxOptions>
        </Transition>
      </Combobox>
    </div>
  );
}

export default QuickSearch;
